import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  apiFetch,
  configureAuthSession,
  type SessionTeam,
  type TeamContext,
  type TokenResponse,
} from "@/lib/api";
import { useProjectStore } from "@/store/project";
import { useRecreateStore } from "@/store/recreate";

type AuthStatus = "checking" | "authenticated" | "unauthenticated";
type AuthUser = TokenResponse["user"];

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  activeTeam: SessionTeam | null;
  teams: SessionTeam[];
  passwordChangeToken: string | null;
  status: AuthStatus;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<TokenResponse>;
  register: (email: string, password: string, fullName?: string) => Promise<TokenResponse>;
  activate: (token: string, password: string) => Promise<void>;
  completePasswordChange: (password: string) => Promise<void>;
  switchTeam: (teamId: string) => Promise<void>;
  logout: () => void;
}

let initializePromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      activeTeam: null,
      teams: [],
      passwordChangeToken: null,
      status: "checking",
      initialize: () => {
        if (initializePromise) return initializePromise;

        initializePromise = (async () => {
          // One-time migration from the previous duplicated localStorage keys.
          // Prefer them because they may contain a token rotated after the
          // persisted Zustand snapshot was written.
          const legacyToken = localStorage.getItem("token");
          const legacyRefreshToken = localStorage.getItem("refresh_token");
          if (legacyToken || legacyRefreshToken) {
            set({
              token: legacyToken || get().token,
              refreshToken: legacyRefreshToken || get().refreshToken,
            });
          }
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");

          if (!get().token && !get().refreshToken) {
            get().logout();
            return;
          }

          try {
            const user = await apiFetch<AuthUser>("/api/auth/me");
            const teamContexts = await apiFetch<TeamContext[]>("/api/teams");
            const teams = teamContexts.map(({ id, name, slug, role }) => ({ id, name, slug, role }));
            const activeTeam = teams.find((team) =>
              teamContexts.some((context) => context.id === team.id && context.current)
            ) ?? teams[0] ?? null;
            set({ user, teams, activeTeam, status: "authenticated" });
          } catch {
            // apiFetch clears rejected sessions. A temporary network failure,
            // however, must not destroy an otherwise complete local session.
            if (get().status === "checking") {
              if (get().token && get().user) set({ status: "authenticated" });
              else get().logout();
            }
          }
        })();

        return initializePromise;
      },
      login: async (email, password) => {
        const data = await apiFetch<TokenResponse>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password }),
        });
        useRecreateStore.getState().clearRecreate();
        if (data.requires_password_change) {
          set({
            token: null,
            refreshToken: null,
            user: data.user,
            activeTeam: data.active_team,
            teams: data.teams,
            passwordChangeToken: data.password_change_token,
            status: "unauthenticated",
          });
        } else if (data.access_token && data.refresh_token) {
          set({
            token: data.access_token,
            refreshToken: data.refresh_token,
            user: data.user,
            activeTeam: data.active_team,
            teams: data.teams,
            passwordChangeToken: null,
            status: "authenticated",
          });
        }
        return data;
      },
      register: async (email, password, fullName) => {
        const data = await apiFetch<TokenResponse>("/api/auth/register", {
          method: "POST",
          body: JSON.stringify({ email, password, full_name: fullName }),
        });
        if (!data.access_token || !data.refresh_token) throw new Error("Registration did not create a session");
        set({
          token: data.access_token,
          refreshToken: data.refresh_token,
          user: data.user,
          activeTeam: data.active_team,
          teams: data.teams,
          passwordChangeToken: null,
          status: "authenticated",
        });
        return data;
      },
      activate: async (token, password) => {
        const data = await apiFetch<TokenResponse>("/api/auth/activate", {
          method: "POST",
          body: JSON.stringify({ token, password }),
        });
        if (!data.access_token || !data.refresh_token) throw new Error("Activation did not create a session");
        set({
          token: data.access_token,
          refreshToken: data.refresh_token,
          user: data.user,
          activeTeam: data.active_team,
          teams: data.teams,
          passwordChangeToken: null,
          status: "authenticated",
        });
      },
      completePasswordChange: async (password) => {
        const passwordChangeToken = get().passwordChangeToken;
        if (!passwordChangeToken) throw new Error("Password change session expired. Sign in again.");
        const data = await apiFetch<TokenResponse>("/api/auth/complete-password-change", {
          method: "POST",
          body: JSON.stringify({ password_change_token: passwordChangeToken, password }),
        });
        if (!data.access_token || !data.refresh_token) throw new Error("Password change did not create a session");
        set({
          token: data.access_token,
          refreshToken: data.refresh_token,
          user: data.user,
          activeTeam: data.active_team,
          teams: data.teams,
          passwordChangeToken: null,
          status: "authenticated",
        });
      },
      switchTeam: async (teamId) => {
        const data = await apiFetch<TokenResponse>("/api/auth/switch-team", {
          method: "POST",
          body: JSON.stringify({ team_id: teamId }),
        });
        if (!data.access_token || !data.refresh_token) throw new Error("Team switch did not create a session");
        useProjectStore.getState().setSelectedProject(null);
        useRecreateStore.getState().clearRecreate();
        set({
          token: data.access_token,
          refreshToken: data.refresh_token,
          user: data.user,
          activeTeam: data.active_team,
          teams: data.teams,
          status: "authenticated",
        });
      },
      logout: () => {
        useRecreateStore.getState().clearRecreate();
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        // Drop the persisted project scope — it belongs to the previous account and
        // would otherwise leak into the next session (Generate → NotFoundError).
        useProjectStore.getState().setSelectedProject(null);
        set({
          token: null,
          refreshToken: null,
          user: null,
          activeTeam: null,
          teams: [],
          passwordChangeToken: null,
          status: "unauthenticated",
        });
      },
    }),
    {
      name: "auth-storage",
      partialize: ({ token, refreshToken, user, activeTeam, teams }) => ({
        token,
        refreshToken,
        user,
        activeTeam,
        teams,
      }),
    }
  )
);

configureAuthSession({
  getAccessToken: () => useAuthStore.getState().token,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  replaceSession: (data) => {
    if (!data.access_token || !data.refresh_token) return;
    useAuthStore.setState({
      token: data.access_token,
      refreshToken: data.refresh_token,
      user: data.user,
      activeTeam: data.active_team,
      teams: data.teams,
      status: "authenticated",
    });
  },
  clearSession: () => useAuthStore.getState().logout(),
});
